import React from "react";
import {
  LocalShipping,
  ExpandMore,
  CheckCircle,
  Store,
  ShoppingBag,
  ArrowForward,
  Phone,
  Share,
  Verified,
  Star
} from "@mui/icons-material";

/* ================= TYPES ================= */

interface StoreTheme {
  logo?: string;
  background_image_desktop?: string;
}

interface StoreInfo {
  id: number;
  name: string;
  theme?: StoreTheme;
  rating?: number;
  ordersCount?: number;
  isVerified?: boolean;
}

export type StoreHeaderVariant =
  | "default"
  | "premium"
  | "mobile"
  | "compact"
  | "minimal";

interface StoreHeaderProps {
  storeId: number;
  store: StoreInfo | null;
  subtotal?: number;
  itemCount?: number;
  isSelected?: boolean;
  isExpanded?: boolean;
  isRTL?: boolean;
  variant?: StoreHeaderVariant;

  t: (key: string) => string;
  currency?: (amount: number) => string;

  onToggleExpand?: () => void;
  onStoreClick?: () => void;
  onContactStore?: () => void;
  onShareStore?: () => void;

  className?: string;
}

/* ================= SHARED ================= */

const StoreLogo = ({
  src,
  isSelected,
  isVerified,
  size = "md"
}: {
  src: string;
  isSelected?: boolean;
  isVerified?: boolean;
  size?: "sm" | "md" | "lg";
}) => {
  const sizes = {
    sm: "w-10 h-10",
    md: "w-14 h-14",
    lg: "w-20 h-20"
  };

  return (
    <div className="relative flex-shrink-0">
      <div className={`${sizes[size]} rounded-xl overflow-hidden`}>
        <img src={src} className="w-full h-full object-cover" />
      </div>

      {isSelected && (
        <div className="absolute -bottom-1 -right-1 bg-emerald-500 text-white rounded-full p-1">
          <CheckCircle fontSize="small" />
        </div>
      )}

      {isVerified && (
        <div className="absolute -top-1 -left-1 bg-blue-500 text-white rounded-full p-1">
          <Verified fontSize="small" />
        </div>
      )}
    </div>
  );
};

/* ================= VARIANTS ================= */

const MobileStoreHeader: React.FC<StoreHeaderProps> = ({
  store,
  subtotal = 0,
  itemCount = 0,
  isSelected,
  t,
  currency,
  onStoreClick,
  className
}) => {
  return (
    <div className={`bg-white p-4 rounded-xl border ${className}`}>
      <div className="flex items-center gap-3">
        <StoreLogo
          src={store?.theme?.logo || "/placeholder-store.png"}
          isSelected={isSelected}
          isVerified={store?.isVerified}
        />

        <div className="flex-1 min-w-0">
          <h3 className="font-bold truncate">{store?.name}</h3>
          <p className="text-xs text-gray-500">
            {itemCount} {t("cart.items")}
          </p>
        </div>

        <button
          onClick={onStoreClick}
          className="text-sm text-emerald-600 font-medium"
        >
          {t("cart.view")}
        </button>
      </div>

      {currency && (
        <div className="mt-3 text-lg font-bold">
          {currency(subtotal)}
        </div>
      )}
    </div>
  );
};

const CompactStoreHeader: React.FC<StoreHeaderProps> = ({
  store,
  itemCount = 0,
  subtotal = 0,
  isSelected,
  isExpanded,
  isRTL,
  t,
  currency,
  onToggleExpand
}) => (
  <div
    className={`flex items-center justify-between p-3 rounded-lg border ${
      isSelected ? "border-emerald-300 bg-emerald-50" : "border-gray-200"
    }`}
  >
    <div className={`flex items-center gap-2 ${isRTL ? "flex-row-reverse" : ""}`}>
      <StoreLogo
        src={store?.theme?.logo || "/placeholder-store.png"}
        isSelected={isSelected}
        size="sm"
      />

      <div className="min-w-0">
        <h4 className="text-sm font-bold truncate">{store?.name}</h4>
        <span className="text-xs text-gray-500">
          {itemCount} {t("cart.items")}
        </span>
      </div>
    </div>

    <div className="flex items-center gap-2">
      {currency && (
        <span className="font-bold text-sm">{currency(subtotal)}</span>
      )}
      <button onClick={onToggleExpand}>
        <ExpandMore
          className={`transition-transform ${
            isExpanded ? "rotate-180" : ""
          }`}
        />
      </button>
    </div>
  </div>
);

const MinimalStoreHeader: React.FC<StoreHeaderProps> = ({
  store,
  itemCount = 0,
  isSelected,
  t
}) => (
  <div className="flex items-center justify-between p-2">
    <div className="flex items-center gap-2">
      <img
        src={store?.theme?.logo || "/placeholder-store.png"}
        className="w-8 h-8 rounded-md object-cover"
      />
      <span className="text-sm font-medium">{store?.name}</span>
      <span className="text-xs text-gray-500">
        {itemCount} {t("cart.items")}
      </span>
    </div>

    {isSelected && <CheckCircle className="text-emerald-500 text-sm" />}
  </div>
);

/* ================= DEFAULT / PREMIUM ================= */

const DefaultStoreHeader: React.FC<StoreHeaderProps> = ({
  store,
  subtotal = 0,
  itemCount = 0,
  isSelected,
  isExpanded,
  isRTL,
  t,
  currency,
  onToggleExpand,
  onStoreClick
}) => (
  <div className="flex justify-between items-center pb-4 border-b">
    <div className={`flex items-center gap-3 ${isRTL ? "flex-row-reverse" : ""}`}>
      <StoreLogo
        src={store?.theme?.logo ||  store?.theme?.background_image_desktop || "/placeholder-store.png"}
        isSelected={isSelected}
      />

      <div>
        <h3
          className="font-bold cursor-pointer"
          onClick={onStoreClick}
        >
          {store?.name}
        </h3>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <LocalShipping fontSize="small" />
          {itemCount} {t("cart.items")}
        </div>
      </div>
    </div>

    <div className="text-right">
      {currency && (
        <div className="font-bold text-lg">{currency(subtotal)}</div>
      )}
      <button
        onClick={onToggleExpand}
        className="text-sm text-gray-500 flex items-center gap-1"
      >
        {t(isExpanded ? "cart.hideItems" : "cart.showItems")}
        <ExpandMore
          className={`transition-transform ${
            isExpanded ? "rotate-180" : ""
          }`}
        />
      </button>
    </div>
  </div>
);

/* ================= MAIN EXPORT ================= */

const StoreHeader: React.FC<StoreHeaderProps> = (props) => {
  const { variant = "default" } = props;

  switch (variant) {
    case "mobile":
      return <MobileStoreHeader {...props} />;
    case "compact":
      return <CompactStoreHeader {...props} />;
    case "minimal":
      return <MinimalStoreHeader {...props} />;
    default:
      return <DefaultStoreHeader {...props} />;
  }
};

export default StoreHeader;
